/* eslint-disable react/prop-types */
/**
 * TaskDetailsDialog + TaskCompletionNoteDialog — the Task Details drawer
 * (Discussion thread, Withdraw, Mark In Progress, edit, email-verify
 * actions) and its completion-note companion, extracted VERBATIM from
 * App.js (Jun 2026, strangler pattern).  Hosts: legacy App.js AND the
 * V2 TasksPanel — state + handlers arrive via `ctx`.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DebouncedTextarea, DebouncedInput } from '../ui/debounced-textarea';
import { Mail, Pencil } from 'lucide-react';
import { isSuperAdmin } from '../../utils/helpers';
import { formatDateStringForDisplay } from '../../utils/dateHelpers';

export const TaskDetailsDialog = ({ ctx }) => {
  const {
    API,
    clients,
    emailVerifyMode,
    setEmailVerifyMode,
    emailVerifyNewEmail,
    setEmailVerifyNewEmail,
    isEditingTask,
    setIsEditingTask,
    refreshAlerts,
    refreshTodos,
    selectedTask,
    setSelectedTask,
    setShowTaskDetail,
    showTaskDetail,
    setCompletionDialogNote,
    setCompletionDialogTask,
    taskComment,
    setTaskComment,
    taskEditData,
    setTaskEditData,
    setTasks,
    setTasksAssignedByMe,
    updateTaskStatusOptimistic,
    user,
    onReschedule,
  } = ctx;

  return (
      <Dialog open={showTaskDetail} onOpenChange={(open) => {
        setShowTaskDetail(open);
        if (!open) {
          setIsEditingTask(false);
          setTaskEditData({});
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedTask && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 justify-between">
                  <div className="flex items-center gap-2">
                    {selectedTask.title}
                    {selectedTask.priority === 'urgent' && (
                      <Badge className="bg-red-600 text-white">Urgent</Badge>
                    )}
                  </div>
                  {/* Edit button - only show for task creator */}
                  {selectedTask.assigned_by === user?.id && selectedTask.status !== 'completed' && selectedTask.status !== 'dismissed' && !isEditingTask && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsEditingTask(true);
                        setTaskEditData({
                          category: selectedTask.category || 'other',
                          priority: selectedTask.priority || 'normal',
                          due_date: selectedTask.due_date || '',
                          client_id: selectedTask.client_id || '',
                        });
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Task Info - Edit Mode */}
                {isEditingTask ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2 text-blue-800 mb-2">
                      <Pencil className="h-4 w-4" />
                      <span className="font-semibold">Edit Task Details</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Category</Label>
                        <Select 
                          value={taskEditData.category} 
                          onValueChange={(value) => setTaskEditData({...taskEditData, category: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduling">Scheduling</SelectItem>
                            <SelectItem value="client_communication">Client Communication</SelectItem>
                            <SelectItem value="external_coordination">External Coordination</SelectItem>
                            <SelectItem value="administrative">Administrative</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-sm">Priority</Label>
                        <Select 
                          value={taskEditData.priority} 
                          onValueChange={(value) => setTaskEditData({...taskEditData, priority: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="urgent"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>Urgent</span></SelectItem>
                            <SelectItem value="normal"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-teal-500"></span>Normal</span></SelectItem>
                            <SelectItem value="low"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-gray-400"></span>Low</span></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-sm">Due Date</Label>
                        <Input
                          type="date"
                          value={taskEditData.due_date || ''}
                          onChange={(e) => setTaskEditData({...taskEditData, due_date: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label className="text-sm">Related Client</Label>
                        <Select 
                          value={taskEditData.client_id || 'none'} 
                          onValueChange={(value) => setTaskEditData({...taskEditData, client_id: value === 'none' ? '' : value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select client..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No client</SelectItem>
                            {clients.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.full_name} {c.status !== 'active' ? `(${c.status})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 justify-end pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditingTask(false);
                          setTaskEditData({});
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/tasks/${selectedTask.id}`, taskEditData);
                            
                            // Update the selected task optimistically
                            const updatedTask = {
                              ...selectedTask,
                              ...taskEditData,
                              client_name: taskEditData.client_id 
                                ? clients.find(c => c.id === taskEditData.client_id)?.full_name 
                                : null
                            };
                            setSelectedTask(updatedTask);
                            
                            // Update in lists
                            const updateInList = (list) => list?.map(t => 
                              t.id === selectedTask.id ? { ...t, ...taskEditData, client_name: updatedTask.client_name } : t
                            );
                            setTasks(updateInList);
                            setTasksAssignedByMe(updateInList);
                            
                            toast.success('Task updated');
                            setIsEditingTask(false);
                            setTaskEditData({});
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update task');
                          }
                        }}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Task Info - View Mode */
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-semibold">Category:</span>{' '}
                      {selectedTask.category ? (selectedTask.category.replace('_', ' ').charAt(0).toUpperCase() + selectedTask.category.replace('_', ' ').slice(1)) : 'General'}
                    </div>
                    <div>
                      <span className="font-semibold">Priority:</span>{' '}
                      {selectedTask.priority ? (selectedTask.priority.charAt(0).toUpperCase() + selectedTask.priority.slice(1)) : 'Medium'}
                    </div>
                    <div>
                      <span className="font-semibold">Assigned By:</span> {selectedTask.assigned_by_name}
                    </div>
                    <div>
                      <span className="font-semibold">Assigned To:</span> {selectedTask.assigned_to_name}
                    </div>
                    {selectedTask.client_name && (
                      <div className="col-span-2">
                        <span className="font-semibold">Client:</span> {selectedTask.client_name}
                      </div>
                    )}
                    {selectedTask.due_date && (
                      <div className="col-span-2">
                        <span className="font-semibold">Due Date:</span>{' '}
                        <span className={new Date(selectedTask.due_date) < new Date() ? 'text-red-600 font-semibold' : ''}>
                          {formatDateStringForDisplay(selectedTask.due_date)}
                        </span>
                      </div>
                    )}
                    {/* Read Receipt - Show for assigner */}
                    {selectedTask.assigned_by === user?.id && (
                      <div className="col-span-2">
                        <span className="font-semibold">Viewed:</span>{' '}
                        {selectedTask.viewed_at ? (
                          <span className="text-green-600">
                            ✓ {formatDateStringForDisplay(selectedTask.viewed_at)} {new Date(selectedTask.viewed_at).toLocaleTimeString()}
                          </span>
                        ) : (
                          <span className="text-gray-500">Not yet viewed</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {selectedTask.description && (
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm whitespace-pre-wrap">{selectedTask.description}</p>
                  </div>
                )}

                {/* Feb 2026 — Completion outcome.  Surfaces the
                    assignee's note + who closed it + when, so the
                    assigner gets the same info as in their bell
                    notification right inside the task drawer. */}
                {selectedTask.status === 'completed' && (
                  <div
                    className="border border-emerald-300 bg-emerald-50 p-3 rounded space-y-1.5"
                    data-testid="task-completion-summary"
                  >
                    <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold">
                      ✓ Completed
                      {selectedTask.completed_by_name && (
                        <span className="text-emerald-700 font-normal">
                          by {selectedTask.completed_by_name}
                        </span>
                      )}
                      {selectedTask.completed_at && (
                        <span className="text-xs text-emerald-700 font-normal ml-auto">
                          {formatDateStringForDisplay(selectedTask.completed_at)} {new Date(selectedTask.completed_at).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                    {selectedTask.completion_note && (
                      <p className="text-sm text-emerald-900 whitespace-pre-wrap">
                        “{selectedTask.completion_note}”
                      </p>
                    )}
                    {!selectedTask.completion_note && (
                      <p className="text-xs text-emerald-700 italic">
                        No completion note left.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Email Verification UI - Only for verify_email action type */}
                {selectedTask.action_type === 'verify_email' && selectedTask.status !== 'completed' && selectedTask.status !== 'dismissed' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2 text-blue-800">
                      <Mail className="h-5 w-5" />
                      <span className="font-semibold">Email Verification Required</span>
                    </div>
                    
                    <div className="text-sm text-blue-700">
                      <p className="mb-2">Current email: <span className="font-mono bg-blue-100 px-2 py-0.5 rounded">{selectedTask.action_data?.email_address || 'Unknown'}</span></p>
                      {selectedTask.action_data?.bounce_reason && (
                        <p className="text-red-600">Bounce reason: {selectedTask.action_data.bounce_reason}</p>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={emailVerifyMode === 'verify' ? 'default' : 'outline'}
                          onClick={() => setEmailVerifyMode('verify')}
                          className={emailVerifyMode === 'verify' ? 'bg-green-600 hover:bg-green-700' : ''}
                        >
                          ✓ Confirm Email is Valid
                        </Button>
                        <Button
                          size="sm"
                          variant={emailVerifyMode === 'update' ? 'default' : 'outline'}
                          onClick={() => {
                            setEmailVerifyMode('update');
                            setEmailVerifyNewEmail('');
                          }}
                          className={emailVerifyMode === 'update' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                        >
                          ✎ Update Email Address
                        </Button>
                        <Button
                          size="sm"
                          variant={emailVerifyMode === 'invalid' ? 'default' : 'outline'}
                          onClick={() => setEmailVerifyMode('invalid')}
                          className={emailVerifyMode === 'invalid' ? 'bg-red-600 hover:bg-red-700' : ''}
                        >
                          ✗ No Valid Email Available
                        </Button>
                      </div>
                      
                      {emailVerifyMode === 'update' && (
                        <div className="flex gap-2 items-center">
                          <DebouncedInput
                            type="email"
                            placeholder="Enter new email address..."
                            value={emailVerifyNewEmail}
                            onChange={(e) => setEmailVerifyNewEmail(e.target.value)}
                            className="max-w-xs"
                          />
                        </div>
                      )}
                      
                      <Button
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700"
                        disabled={emailVerifyMode === 'update' && !emailVerifyNewEmail}
                        onClick={async () => {
                          try {
                            const params = new URLSearchParams();
                            if (emailVerifyMode === 'verify') {
                              params.append('verified', 'true');
                            } else if (emailVerifyMode === 'update') {
                              params.append('new_email', emailVerifyNewEmail);
                            } else if (emailVerifyMode === 'invalid') {
                              params.append('verified', 'false');
                            }
                            
                            await axios.post(`${API}/clients/${selectedTask.client_id}/verify-email?${params.toString()}`);
                            toast.success(
                              emailVerifyMode === 'verify' 
                                ? 'Email verified successfully' 
                                : emailVerifyMode === 'update'
                                  ? 'Email updated successfully'
                                  : 'Email marked as invalid'
                            );
                            setShowTaskDetail(false);
                            setEmailVerifyMode('verify');
                            setEmailVerifyNewEmail('');
                            refreshTodos(); refreshAlerts();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update email status');
                          }
                        }}
                      >
                        {emailVerifyMode === 'verify' && '✓ Confirm & Complete Task'}
                        {emailVerifyMode === 'update' && '✓ Update Email & Complete Task'}
                        {emailVerifyMode === 'invalid' && '✓ Mark Invalid & Complete Task'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Status Actions - Assignee, Creator, Admin, or Supervisor can manage task status */}
                {/* Feb 27, 2026 — Added `assigned_by === user.id` so a provider who
                    creates a task on a note (which auto-assigns to a scheduler /
                    med-mgr etc.) can later mark it complete or dismiss it
                    themselves.  Backend already allows this (task_routes.py:502-509);
                    UI was the only thing hiding the buttons. */}
                {(() => {
                  // Mirrors backend PUT /tasks/{id} (task_routes.py): role-pool
                  // membership only counts when the task has NO specific
                  // assignee, using the same role normalization.
                  const role = String(user?.role || '').toLowerCase();
                  const userPool = [];
                  if (['biller', 'billing', 'billing_manager'].includes(role) || user?.is_biller || user?.is_billing_manager) userPool.push('biller', 'billing');
                  if (['office', 'office_staff', 'front_desk'].includes(role)) userPool.push('office');
                  const taskRoleRaw = selectedTask.assigned_to_role || selectedTask.assigned_role;
                  const taskRoles = (Array.isArray(taskRoleRaw) ? taskRoleRaw : taskRoleRaw ? [taskRoleRaw] : [])
                    .filter((r) => typeof r === 'string').map((r) => r.toLowerCase());
                  const isRolePoolMember = !selectedTask.assigned_to && taskRoles.some((r) => userPool.includes(r));
                  return selectedTask.assigned_to === user?.id || selectedTask.assigned_by === user?.id || isSuperAdmin(user) || user?.is_supervisor || isRolePoolMember;
                })() && selectedTask.status !== 'completed' && selectedTask.status !== 'dismissed' && (
                  <div className="flex gap-2 flex-wrap">
                    {/* Primary action button — Feb 28, 2026.  Mirrors the
                        V2 Tasks per-row action.  The Task Details
                        dialog should let the user "do the thing" without
                        having to hunt for the right tab. */}
                    {(() => {
                      const t = (selectedTask.type || '').toLowerCase();
                      const cat = (selectedTask.category || '').toLowerCase();
                      const title = (selectedTask.title || '').toLowerCase();
                      const at = (selectedTask.action_type || '').toLowerCase();
                      let label = null;
                      let url = null;
                      let useReschedule = false;
                      if (selectedTask.client_id
                          && (at === 'open_calendar' || cat === 'scheduling'
                              || title.startsWith('reschedule ')
                              || title.startsWith('schedule '))) {
                        label = title.startsWith('reschedule') ? 'Reschedule' : 'Schedule';
                        // Jun 2026 (Phase B) — V2 hosts pass onReschedule
                        // which opens the native appointment dialog on
                        // /v2/calendar; legacy keeps the dashboard bounce.
                        if (typeof onReschedule === 'function') {
                          useReschedule = true;
                        } else {
                          url = `/dashboard?view=appointments&open_client=${selectedTask.client_id}`;
                        }
                      } else if ((at === 'open_discharge' || t === 'discharge_required')
                                 && selectedTask.client_id) {
                        label = 'Open Discharge';
                        url = `/v2/clients/${selectedTask.client_id}?tab=notes&action=discharge`;
                      } else if (at === 'open_treatment_plan' && selectedTask.client_id) {
                        label = 'Open Plan';
                        url = `/v2/clients/${selectedTask.client_id}?tab=treatment-plans`;
                      } else if (at === 'open_client' && selectedTask.client_id) {
                        label = 'Open Chart';
                        url = `/v2/clients/${selectedTask.client_id}`;
                      } else if (at === 'open_note' && (selectedTask.note_id || selectedTask.source_note_id || selectedTask.action_data?.note_id)) {
                        const _noteId = selectedTask.note_id || selectedTask.source_note_id || selectedTask.action_data?.note_id;
                        label = 'Open Note';
                        // Jun 2026 — from a V2 page, open the native V2 note
                        // editor with a return path; legacy keeps the
                        // dashboard deep-link (same rule as TasksPanel).
                        if (window.location.pathname.startsWith('/v2')) {
                          const ret = encodeURIComponent(window.location.pathname + window.location.search);
                          url = `/v2/note/${_noteId}?return=${ret}`;
                        } else {
                          url = `/dashboard?view=notes&open_note=${_noteId}`;
                        }
                      }
                      if (!label) return null;
                      return (
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => {
                            setShowTaskDetail(false);
                            if (useReschedule) onReschedule(selectedTask);
                            else window.location.assign(url);
                          }}
                          data-testid="task-primary-action-btn"
                        >
                          {label}
                        </Button>
                      );
                    })()}
                    {selectedTask.status === 'pending' && (
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          setShowTaskDetail(false);
                          await updateTaskStatusOptimistic(selectedTask.id, 'in_progress');
                        }}
                      >
                        Mark In Progress
                      </Button>
                    )}
                    <Button 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        // Feb 2026 — open the completion-note dialog
                        // (instead of immediately firing the status
                        // change) so the assigner can see what the
                        // assignee actually did.
                        setCompletionDialogTask(selectedTask);
                        setCompletionDialogNote('');
                      }}
                      data-testid="task-mark-complete-btn"
                    >
                      Mark Complete
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        setShowTaskDetail(false);
                        await updateTaskStatusOptimistic(selectedTask.id, 'dismissed');
                      }}
                    >
                      Dismiss
                    </Button>
                  </div>
                )}

                {/* Reopen — undo an accidental complete/dismiss (Jun 2026 user ask) */}
                {(selectedTask.assigned_to === user?.id || selectedTask.assigned_by === user?.id || isSuperAdmin(user) || user?.is_supervisor) && (selectedTask.status === 'completed' || selectedTask.status === 'dismissed') && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                      onClick={async () => {
                        setShowTaskDetail(false);
                        await updateTaskStatusOptimistic(selectedTask.id, 'pending', { skipToast: true });
                        toast.success('Task reopened');
                      }}
                      data-testid="task-reopen-btn"
                    >
                      Reopen Task
                    </Button>
                  </div>
                )}

                {/* Withdraw/Cancel Task - For Assigner */}
                {selectedTask.assigned_by === user?.id && selectedTask.status !== 'completed' && selectedTask.status !== 'dismissed' && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to withdraw this task? The assignee will be notified.')) {
                          setShowTaskDetail(false);
                          await updateTaskStatusOptimistic(selectedTask.id, 'dismissed', { 
                            skipToast: true 
                          });
                          toast.success('Task withdrawn');
                        }
                      }}
                    >
                      Withdraw Task
                    </Button>
                  </div>
                )}
                
                {/* Comments Section */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Discussion</h3>
                  
                  {/* Existing Comments */}
                  <div className="space-y-3 mb-4">
                    {selectedTask.comments && selectedTask.comments.length > 0 ? (
                      selectedTask.comments.map(comment => (
                        <div key={comment.id} className="bg-gray-50 p-3 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{comment.user_name}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(comment.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{comment.comment}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
                    )}
                  </div>
                  
                  {/* Add Comment */}
                  <div className="space-y-2">
                    <DebouncedTextarea
                      placeholder="Add a comment or ask a question..."
                      value={taskComment}
                      onChange={(e) => setTaskComment(e.target.value)}
                      rows={3}
                    />
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!taskComment.trim()) return;
                        
                        try {
                          await axios.post(`${API}/tasks/${selectedTask.id}/comments`, {
                            comment: taskComment
                          });
                          toast.success('Comment added');
                          setTaskComment('');
                          // Refresh task details
                          const response = await axios.get(`${API}/tasks/${selectedTask.id}`);
                          setSelectedTask(response.data);
                          refreshTodos(); refreshAlerts();
                        } catch (error) {
                          toast.error('Failed to add comment');
                        }
                      }}
                    >
                      Add Comment
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
  );
};

export const TaskCompletionNoteDialog = ({ ctx }) => {
  const {
    completionDialogTask,
    setCompletionDialogTask,
    completionDialogNote,
    setCompletionDialogNote,
    completionDialogSubmitting,
    setCompletionDialogSubmitting,
    updateTaskStatusOptimistic,
    setShowTaskDetail,
  } = ctx;

  return (
      <Dialog
        open={!!completionDialogTask}
        onOpenChange={(o) => { if (!o) { setCompletionDialogTask(null); setCompletionDialogNote(''); } }}
      >
        <DialogContent className="max-w-md" data-testid="task-completion-dialog">
          <DialogHeader>
            <DialogTitle>Mark Task Complete</DialogTitle>
            <DialogDescription>
              Optional — add a brief note about what happened so {completionDialogTask?.assigned_by_name || 'the assigner'} knows the outcome.
            </DialogDescription>
          </DialogHeader>
          {completionDialogTask && (
            <div className="space-y-3">
              <div className="text-sm text-slate-700 font-medium">
                {completionDialogTask.title}
              </div>
              <div>
                <Label className="text-xs">Completion note (optional)</Label>
                <Textarea
                  value={completionDialogNote}
                  onChange={(e) => setCompletionDialogNote(e.target.value)}
                  placeholder="e.g. Scheduled for next Tuesday at 2 PM. Left voicemail for client."
                  rows={3}
                  data-testid="task-completion-note-input"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  This text will appear on {completionDialogTask?.assigned_by_name || 'the assigner'}'s notification.
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setCompletionDialogTask(null); setCompletionDialogNote(''); }}
                  disabled={completionDialogSubmitting}
                  data-testid="task-completion-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={completionDialogSubmitting}
                  onClick={async () => {
                    if (!completionDialogTask?.id) return;
                    setCompletionDialogSubmitting(true);
                    try {
                      await updateTaskStatusOptimistic(
                        completionDialogTask.id,
                        'completed',
                        { completionNote: completionDialogNote },
                      );
                      setShowTaskDetail(false);
                      setCompletionDialogTask(null);
                      setCompletionDialogNote('');
                    } finally {
                      setCompletionDialogSubmitting(false);
                    }
                  }}
                  data-testid="task-completion-confirm-btn"
                >
                  {completionDialogSubmitting ? 'Saving…' : 'Mark Complete'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};
